import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { Breadcrumbs } from '../../cdl/Breadcrumbs/Breadcrumbs';
import { Text } from '../../cdl/Text/Text';
import { CustomerGroupMultiSelect } from '../../common/form-elements/CustomerGroupSelect/CustomerGroupMultiSelect';
import { SupplierGroupMultiSelect } from '../../common/form-elements/SupplierGroupSelect/SupplierGroupMultiSelect';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { Page } from '../../common/Page/Page';
import { DebouncedSearchTextInput } from '../../common/Search/DebouncedSearchTextInput/DebouncedSearchTextInput';
import { Box } from '../../common/ui/Box';

import { AdminMembersTable } from './components/AdminMembersTable';
import { InviteUserButton } from './components/InviteUserButton';

export const AdminMembersPage = () => {
    const role = useRole();

    const search = useSearch({ from: '/_app/members/' });
    const navigate = useNavigate({ from: '/members' });

    useDocumentTitle(translate('page.membersOverview'));

    return (
        <Page>
            <Box display="grid" rowGap={16}>
                <Box display="grid" gap={4}>
                    <Breadcrumbs
                        links={[{ label: translate('settings.title'), linkOptions: linkOptions({ to: '/settings' }) }]}
                    />

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Text variant="headline" as="h1">
                            {translate('members')}
                        </Text>
                        {role.hasAnyAdminRights() ? <InviteUserButton /> : null}
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between">
                    <Box display="flex" columnGap={8} alignItems="center">
                        <Box minWidth="250px">
                            <DebouncedSearchTextInput
                                placeholder={translate('user.searchPlaceholder')}
                                onChange={(searchQuery) => {
                                    navigate({
                                        search: (previousSearch) => ({ ...previousSearch, searchQuery, page: 0 }),
                                    });
                                }}
                                value={search.searchQuery}
                            />
                        </Box>

                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={8} minWidth="600px">
                            <CustomerGroupMultiSelect
                                onChange={(customerGroupIds) =>
                                    navigate({
                                        search: (previousSearch) => ({
                                            ...previousSearch,
                                            customerGroupIds: customerGroupIds.map((id) => id),
                                            page: 0,
                                        }),
                                    })
                                }
                                value={search.customerGroupIds}
                                placeholder={translate('customerGroupSelect.filterByCustomerGroups')}
                                clearSearchOnSelect={false}
                                dropdownWidth="520px"
                            />

                            <SupplierGroupMultiSelect
                                onChange={(supplierGroupIds) => {
                                    if (Array.isArray(supplierGroupIds)) {
                                        navigate({
                                            search: (previousSearch) => ({
                                                ...previousSearch,
                                                supplierGroupIds: supplierGroupIds,
                                                page: 0,
                                            }),
                                        });
                                    }
                                }}
                                value={search.supplierGroupIds}
                                placeholder={translate('supplierGroupSelect.filterBySupplierGroups')}
                                clearSearchOnSelect={false}
                                dropdownWidth="520px"
                            />
                        </Box>
                    </Box>
                </Box>

                <AdminMembersTable />
            </Box>
        </Page>
    );
};
